import React, { useEffect, useState } from "react";

import NoLicense from "@/components/AppContext/License/NoLicense";
import WithLicenseBanner from "@/components/AppContext/License/WithLicenseBanner";
import { plans } from "@/lib/cpp-plans";
import type { MondayPlan, MondayService } from "@/services/monday-service";

import { useCurrentMondayContext } from "./WithMondayContextAvailable";

interface Props {
  children: React.ReactNode;
  mondayService: MondayService;
}

// If license if valid, it will display the child components (our app content).
// If not it will replace the child components with a NoLicense page instead.
// If license is about to expire, it will display a banner with a message but still
// display the child components.
export default function WithValidLicense({ children, mondayService }: Props) {
  const { subscription } = useCurrentMondayContext();
  const [plan, setPlan] = useState<MondayPlan>();
  // TODO: Find a way to assert this is set; if not it's a build/env config issue
  const disableLicenseChecks = import.meta.env.VITE_DISABLE_LICENSE_CHECKS === "true";

  useEffect(() => {
    // TODO: replace with react-query (useQuery) solution
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    void (async () => {
      try {
        const plan = await mondayService.getMondayPlan();
        setPlan(plan);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [mondayService]);

  const daysLeft = subscription ? subscription.days_left : -1;
  const planMaxUsers = plan ? plan.max_users : 1000000;
  const trial = subscription ? subscription.is_trial : false;
  const userLimitExceeded = plan && subscription ? plans[subscription.plan_id] < planMaxUsers : false;

  if (daysLeft < 0) {
    return disableLicenseChecks ? <>{children}</> : <LicenseExpired />;
  }

  if (trial && daysLeft < 7) {
    return disableLicenseChecks ? <>{children}</> : <TrialAboutToExpire children={children} daysLeft={daysLeft} />;
  }

  if (!trial && userLimitExceeded) {
    return disableLicenseChecks ? <>{children}</> : <UserLimitReached />;
  }

  return <>{children}</>;
}

function LicenseExpired() {
  return (
    <WithLicenseBanner message={bannerMessages.licenseRequired}>
      <NoLicense message={appBlockerMessages.unlicensed} />
    </WithLicenseBanner>
  );
}

function UserLimitReached() {
  return (
    <WithLicenseBanner message={bannerMessages.licenseRequired}>
      <NoLicense message={appBlockerMessages.userLimitExceeded} />
    </WithLicenseBanner>
  );
}

function TrialAboutToExpire({ children, daysLeft }: { children: React.ReactNode; daysLeft: number }) {
  return <WithLicenseBanner message={bannerMessages.trialAboutToExpire(daysLeft)}>{children}</WithLicenseBanner>;
}

// Exported for testing
export const bannerMessages = {
  licenseRequired: "License Required",
  trialAboutToExpire: (daysLeft: number) => {
    const at = daysLeft <= 0 ? "today" : daysLeft === 1 ? "tomorrow" : `in ${daysLeft} days`;
    return `Your trial period expires ${at}.`;
  },
};

// Exported for testing
export const appBlockerMessages = {
  unlicensed:
    "Your app license has expired. Please purchase the app license to continue using Clone Plus - Copy Items.",
  userLimitExceeded:
    "You are exceeding the limit of users using the app in your account. Please upgrade your app license to continue using Clone Plus - Copy Items",
};
